// ── SHOP PROFILE: shape, limits and validation ─────────────────────────────
// Single source of truth for a shop's profile, exactly as constants/designer.js
// is for a designer. The registration step, the Settings editor, the public
// profile and the search filters all read from here, so a limit can never drift
// between where it is enforced and where it is shown.
//
// WHY A SEPARATE FILE rather than more fields on the designer shape: the two
// describe different things. A designer has styles, experience and an hourly
// rate; a shop has categories, a delivery flag and a storefront. Merging them
// would give every profile a dozen fields that are always empty and every form
// a branch on `userType` around each one.

// Stable machine keys — these are what get written to Firestore and filtered
// against. The human label comes from i18n (`shop_cat_<key>`), so a category can
// be renamed or translated without migrating a single document.
export const SHOP_CATEGORIES = [
  'building_materials',   // cement, plaster, insulation, timber
  'furniture',
  'openings',             // windows and doors
  'walls',                // paint, wallpaper, panels
  'floor',                // parquet, laminate, tile, carpet
  'ceiling',
  'lighting',             // chandeliers and fixtures
  'electronics',          // appliances, smart home
  'plumbing',             // sinks, taps, sanitary ware
  'tiles',
  'textiles',             // curtains, rugs, upholstery
  'decor',
  'kitchen',
  'bathroom',
  'garden',               // outdoor and landscaping
  'tools',
];

export const SHOP_LIMITS = {
  name:         { min: 2, max: 80 },
  // Same 250 as the designer bio, and for the same reason: it is rendered IN
  // FULL in the profile header, and a limit you can display beats a longer one
  // you have to truncate.
  description:  { max: 250 },
  location:     { max: 120 },
  locationAddress: { max: 250 },
  website:      { max: 200 },
  categories:   { max: 8 },      // keeps the profile card and search chip row readable
  socialValue:  { max: 120 },
};

// ── SOCIAL AND CONTACT CHANNELS ──────────────────────────────────────────────
// A FIXED LIST, not free-form contact rows. The designer profile takes an open
// `contacts` array because a designer's contact could be anything; a shop's are
// the same five or six every time, and a fixed list is what lets the profile
// render the right icon, build the right link, and lets search offer "has a
// Telegram" as a filter later without parsing strings.
//
// `prefix` is what turns a stored handle into a URL. A value already starting
// with http is used as-is, so pasting a full link works as well as typing a
// handle — see shopSocialHref below.
export const SHOP_SOCIALS = [
  { id: 'phone',     prefix: 'tel:',                  free: true },
  { id: 'instagram', prefix: 'https://instagram.com/' },
  { id: 'facebook',  prefix: 'https://facebook.com/'  },
  { id: 'tiktok',    prefix: 'https://tiktok.com/@'   },
  { id: 'telegram',  prefix: 'https://t.me/'          },
];

export const SHOP_SOCIAL_IDS = SHOP_SOCIALS.map(s => s.id);

/**
 * The href for one stored social value.
 *
 * Handles are stored, not URLs — "@shop", "shop" and
 * "https://instagram.com/shop" all mean the same account, and normalising at
 * read time means the stored value stays whatever the shop typed. A value that
 * is already a URL is passed straight through; `phone` is `free`, so it is
 * never prefixed with a site.
 */
export function shopSocialHref(id, value) {
  const v = String(value || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  const meta = SHOP_SOCIALS.find(s => s.id === id);
  if (!meta) return '';
  if (meta.free) return `${meta.prefix}${v.replace(/\s+/g, '')}`;
  return `${meta.prefix}${v.replace(/^@+/, '')}`;
}

/** A website typed without a scheme is still a website. */
export function shopWebsiteHref(value) {
  const v = String(value || '').trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

/**
 * Normalises whatever the form holds into exactly what Firestore should store.
 *
 * Mirrors serializeDesignerProfile: every field is clamped, every list is
 * filtered to known keys, and anything unrecognised is dropped rather than
 * written through. The form is not trusted to have done any of this — it is
 * the only thing standing between a typo and a document nothing can query.
 */
export function serializeShopProfile(p) {
  const socials = {};
  for (const { id } of SHOP_SOCIALS) {
    const v = String(p?.socials?.[id] || '').trim().slice(0, SHOP_LIMITS.socialValue.max);
    // Empty channels are omitted entirely rather than stored as '', so the
    // profile can render "the ones that are set" without filtering blanks.
    if (v) socials[id] = v;
  }

  return {
    name: String(p?.name || '').trim().slice(0, SHOP_LIMITS.name.max),
    description: String(p?.description || '').trim().slice(0, SHOP_LIMITS.description.max),
    location: String(p?.location || '').trim().slice(0, SHOP_LIMITS.location.max),
    // The full picked line — see serializeDesignerProfile, same split for the
    // same reason: `location` is the coarse key search groups on, this is the
    // precise address a customer needs in order to actually turn up.
    locationAddress: String(p?.locationAddress || p?.location || '')
      .trim().slice(0, SHOP_LIMITS.locationAddress.max),
    locationCoords:
      p?.locationCoords && Number.isFinite(p.locationCoords.lat) && Number.isFinite(p.locationCoords.lng)
        ? { lat: p.locationCoords.lat, lng: p.locationCoords.lng }
        : null,
    website: String(p?.website || '').trim().slice(0, SHOP_LIMITS.website.max),
    categories: Array.isArray(p?.categories)
      ? [...new Set(p.categories)].filter(c => SHOP_CATEGORIES.includes(c)).slice(0, SHOP_LIMITS.categories.max)
      : [],
    delivery: !!p?.delivery,
    socials,
  };
}

/** A blank profile for a new shop, so the form always has every key. */
export function emptyShopProfile(name = '') {
  return {
    name,
    description: '',
    location: '',
    locationAddress: '',
    locationCoords: null,
    website: '',
    categories: [],
    delivery: false,
    socials: Object.fromEntries(SHOP_SOCIAL_IDS.map(id => [id, ''])),
  };
}

/**
 * What must be filled in before a shop is publishable.
 *
 * Deliberately short. A name says who they are and a category says what they
 * sell, and those two are what search and the recommendation rail need to show
 * them at all — everything else is enrichment the shop can add later. Blocking
 * on a full profile would just mean fewer completed profiles.
 */
export function validateShopProfile(p) {
  const errors = {};
  const name = String(p?.name || '').trim();
  if (name.length < SHOP_LIMITS.name.min) errors.name = 'shop_err_name';
  if (!Array.isArray(p?.categories) || p.categories.length === 0) errors.categories = 'shop_err_categories';
  return errors;
}
